import { Image, Popconfirm } from "antd";
import moment from "moment";
import { AiOutlineRobot } from "react-icons/ai";
import { useCheckChatPermission } from "@lhd/hooks";
import AiStructuredMessage from "./AiStructuredMessage";
import { AiRenderPayload, parseAiRenderPayload } from "./aiNavigation";

export interface IAttachment {
  content_type: string;
  file_size: number;
  file_url: string;
  filename: string;
  is_document: boolean;
  is_image: boolean;
  is_video: boolean;
  id: string;
}

export interface IMessageItem {
  id: string;
  thread_id: string;
  created_by?: string;
  content: string;
  created_at: string;
  user_id?: string | number | null;
  sender_type?: string | null;
  sender_display_name?: string | null;
  source_refs?: unknown;
  ai_render_payload?: AiRenderPayload | null;
  attachments?: IAttachment[];
  soft_deleted_at?: string | null;
  deleted_by_super_admin?: boolean;
}
type MessageItemProps = {
  data: IMessageItem;
  userId: string;
  handleDeleteMessage?: (messageId: string) => void;
};

export const isAiMessage = (message?: Partial<IMessageItem> | null) =>
  message?.sender_type === "ai";

export const isOwnHumanMessage = (
  message: Partial<IMessageItem> | null | undefined,
  userId: string,
) => !isAiMessage(message) && String(message?.user_id) === String(userId);

const MessageItem = ({
  data,
  userId,
  handleDeleteMessage,
}: MessageItemProps) => {
  const { checkPermission } = useCheckChatPermission();
  const isAi = isAiMessage(data);
  const isSendMessage = isOwnHumanMessage(data, userId);
  const senderName = isAi
    ? data?.sender_display_name || "ビズAI"
    : data?.created_by;
  const renderFile = (attachment: IAttachment) => {
    if (attachment.is_image) {
      return (
        <Image
          key={attachment.id}
          src={attachment.file_url}
          alt={attachment.filename}
          className="chat-image"
        />
      );
    } else if (attachment.is_video) {
      return (
        <video
          key={attachment.id}
          src={attachment.file_url}
          className="chat-video"
          controls
          preload="metadata"
          playsInline
        />
      );
    } else {
      return (
        <div style={{ width: "100%" }}>
          <a
            key={attachment.id}
            href={attachment.file_url}
            download={attachment.filename}
            className="chat-file"
            target="_blank"
            rel="noopener noreferrer"
          >
            <span className="chat-file__name">{attachment.filename}</span>
          </a>
        </div>
      );
    }
  };

  const handleLinkClick = async (
    e: React.MouseEvent<HTMLAnchorElement>,
    urlStr: string,
  ) => {
    try {
      const url = new URL(urlStr);
      const threadId = url.searchParams.get("chat_thread_id");
      if (threadId) {
        e.preventDefault();
        const threadData = await checkPermission(threadId);
        if (threadData) {
          const event = new CustomEvent("openChatThread", {
            detail: { thread: threadData },
          });
          window.dispatchEvent(event);
        }
      }
    } catch (err) {
      console.warn("[handleLinkClick] Invalid URL:", urlStr, err);
    }
  };

  const renderAiContent = () => {
    const payload = parseAiRenderPayload(data.ai_render_payload);

    if (payload) {
      return <AiStructuredMessage payload={payload} />;
    }

    return <span className="ai-message-plain-text">{data?.content}</span>;
  };

  return (
    <div
      className={`chat-content__item ${
        isSendMessage ? "chat-content__itemSend" : ""
      } ${isAi ? "chat-content__itemAi" : ""}`}
      data-sender-type={isAi ? "ai" : "human"}
      aria-label={isAi ? "ビズAIの回答" : undefined}
      data-testid={isAi ? `bizai-message-${data.id}` : undefined}
    >
      {isAi ? (
        <div className="chat-ai-avatar" aria-hidden="true">
          <AiOutlineRobot />
        </div>
      ) : (
        !isSendMessage && <img src="/images/iconAvatar.svg" alt="icon-avatar" />
      )}

      <div
        className="message"
        data-created-by={data?.created_by || undefined}
        data-sender-type={isAi ? "ai" : "human"}
      >
        <p>
          {!isSendMessage && senderName && (
            <span className={isAi ? "message-sender-ai" : undefined}>
              {senderName}
            </span>
          )}
          {isAi && <span className="message-ai-badge">AI回答</span>}
          <span>{moment(data?.created_at).format("YYYY/MM/DD")}</span>
        </p>
        <div className="message-content">
          {isAi
            ? renderAiContent()
            : data?.content?.split(/(https?:\/\/[^\s]+)/g).map((part, i) =>
                part.match(/https?:\/\/[^\s]+/) ? (
                  <a
                    key={i}
                    href={part}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => handleLinkClick(e, part)}
                    style={{
                      color: "#1890ff",
                      textDecoration: "underline",
                    }}
                  >
                    {part}
                  </a>
                ) : (
                  part
                ),
              )}
        </div>
        {data.attachments && data.attachments?.length > 0 && (
          <Image.PreviewGroup>
            <div className="chat-image-wrapper">
              {data.attachments?.map((item) => renderFile(item))}
            </div>
          </Image.PreviewGroup>
        )}
        {isSendMessage &&
          !isAi &&
          !data?.soft_deleted_at &&
          !data?.deleted_by_super_admin && (
            <div className="del-message">
              <Popconfirm
                title={<div style={{ width: 140 }}>本当に削除しますか</div>}
                onConfirm={() => {
                  handleDeleteMessage?.(data?.id);
                }}
                okText={<span style={{ width: 60 }}>はい</span>}
                cancelText={<span style={{ width: 60 }}>いいえ</span>}
                okButtonProps={{ style: { order: 1 } }}
                cancelButtonProps={{
                  style: { order: 2, marginLeft: 8 },
                }}
              >
                <img
                  src="/images/iconTrash.svg"
                  alt="icon-trash"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                />
              </Popconfirm>
            </div>
          )}
      </div>
    </div>
  );
};
export default MessageItem;
